import { useEffect, useMemo, useState } from "react";

import usePlatformStore from "@store/usePlatformStore";
import { useUserpilot } from "@platform/hooks/useUserpilot";
import { useGetEmail } from "@screens/sync-email/hooks/useGetEmail";
import { useGetThread } from "./hooks/useGetThread";
import { useGetUser } from "@screens/connect-meridian/hooks/useGetUser";
import { useSyncEmail } from "./hooks/useSyncEmail";
import { SyncEmailActiveScreen, SyncEmailScreenAction } from "@enums/screens.enum";
import SyncToExistingThreadScreen from "./SyncToExistingThreadScreen";
import SyncFirstEmailScreen from "./SyncFirstEmailScreen";
import EmailLoadingScreen from "./EmailLoadingScreen";
import IssueScreen from "./IssueScreen";
import { TransitionWrapper } from "@ui/Transition";

export default function SyncEmailScreen() {
  const { track } = useUserpilot();
  const { itemId, isComposeMode } = usePlatformStore();
  const { isLoading: isEmailLoading } = useGetEmail();
  const { data: thread, isLoading: isThreadLoading } = useGetThread();
  const [activeScreen, setActiveScreen] = useState(SyncEmailActiveScreen.Syncing);
  const [action, setAction] = useState<SyncEmailScreenAction | null>(null);
  const { data: user } = useGetUser(false);
  const autoSubscribeEmailThreads =
    user?.organization?.settings?.emails_thread_auto_subscribe ?? false;

  const { mutate: syncEmailMutate, isPending } = useSyncEmail(
    () => setActiveScreen(SyncEmailActiveScreen.Issue)
  );

  const handleSync = () => {
    setAction(SyncEmailScreenAction.Sync);
    syncEmailMutate({
      identifier: itemId,
      sync_thread: false,
      is_draft: isComposeMode,
    });
    track("Click: Save email" + (isComposeMode ? " (compose mode)" : ""));
  };

  const handleSubscribe = () => {
    setAction(SyncEmailScreenAction.Subscribe);
    syncEmailMutate({
      identifier: itemId,
      sync_thread: true,
      is_draft: isComposeMode,
    });
    track("Click: Subscribe full thread" + (isComposeMode ? " (compose mode)" : ""));
  };

  const handleTryAgain = async () => {
    setActiveScreen(SyncEmailActiveScreen.Syncing);

    if (action === SyncEmailScreenAction.Sync) {
      handleSync();
    } else if (action === SyncEmailScreenAction.Subscribe) {
      handleSubscribe();
    }
  };

  useEffect(() => {
    setAction(autoSubscribeEmailThreads ? SyncEmailScreenAction.Subscribe : null);
  }, [autoSubscribeEmailThreads]);
  const isDataReady = !isEmailLoading && !isThreadLoading;
  const showAnyScreen = activeScreen === SyncEmailActiveScreen.Syncing;

  const showEmailLoadingScreen = showAnyScreen && !isDataReady;
  const showSyncFirstEmailScreen = showAnyScreen && isDataReady && !thread?.synced_emails_count;
  const showSyncToExistingThreadScreen = showAnyScreen && isDataReady && !!thread?.synced_emails_count;

  const screenKey = useMemo(() => {
    if (showEmailLoadingScreen) return "checking";
    if (showSyncFirstEmailScreen) return "sync-first";
    if (showSyncToExistingThreadScreen) return "sync-existing";
    if (activeScreen === SyncEmailActiveScreen.Issue) return "issue";
    return "checking";
  }, [showEmailLoadingScreen, showSyncFirstEmailScreen, showSyncToExistingThreadScreen, activeScreen]);

  const getScreen = () => {
    if (showEmailLoadingScreen) return <EmailLoadingScreen />;

    const syncScreen = (() => {
      if (showSyncFirstEmailScreen) {
        return (
          <SyncFirstEmailScreen
            isPending={isPending}
            onSave={handleSync}
            onSubscribe={handleSubscribe}
            currentAction={action}
          />
        );
      }

      if (showSyncToExistingThreadScreen) {
        return (
          <SyncToExistingThreadScreen
            isPending={isPending}
            onSave={handleSync}
            onSubscribe={handleSubscribe}
            currentAction={action}
            syncedEmailsCount={thread?.synced_emails_count || 0}
            isThreadSubscribed={!!thread?.sync_thread}
          />
        );
      }

      if (activeScreen === SyncEmailActiveScreen.Issue) {
        return <IssueScreen onTryAgain={handleTryAgain} />;
      }

      return <EmailLoadingScreen />;
    })();

    return (
      <div className="flex flex-col">
        <div className="flex flex-col items-center justify-center pt-6 pb-0 px-4">
          {syncScreen}
        </div>
      </div>
    );
  };

  return (
    <TransitionWrapper screenKey={screenKey}>
      {getScreen()}
    </TransitionWrapper>
  );
}
